import { useQuery } from '@tanstack/react-query';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api/axios';

import { VariableAutocomplete } from '../VariableAutocomplete';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { ScheduleWorkflowNodeData } from '@repo/shared-types';

interface ScheduleWorkflowConfigProps {
  data: ScheduleWorkflowNodeData;
  onChange: (data: ScheduleWorkflowNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
}

const useWorkflowOptions = () =>
  useQuery({
    queryKey: ['workflows', 'options'],
    queryFn: async () => {
      const res = await apiClient.get<{ id: string; name: string }[]>('/workflows');
      return res.data;
    },
  });

const TIMEZONES = [
  'Europe/London',
  'UTC',
  'Europe/Dublin',
  'Europe/Paris',
  'Asia/Riyadh',
  'America/New_York',
];

export const ScheduleWorkflowConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
}: ScheduleWorkflowConfigProps) => {
  const { data: workflows } = useWorkflowOptions();
  const update = (fields: Partial<ScheduleWorkflowNodeData>) => onChange({ ...data, ...fields });
  const pickerProps = {
    responseFieldsSchema,
    reviewNodeMetas,
    classifierNodeMetas,
    qualityNodeMetas,
    agentNodeMetas,
    analyticsNodeMetas,
  };

  return (
    <div className="space-y-4">
      <p className="text-[10px] text-gray-500">
        Queues a run of another workflow for this contact at a later time. The target workflow reads
        the contact fresh when it runs, so put your opt-out and &ldquo;already with a
        salesperson&rdquo; checks there.
      </p>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Label</label>
        <Input value={data.label || ''} onChange={(e) => update({ label: e.target.value })} />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Workflow to run</label>
        <Select value={data.workflowId || ''} onValueChange={(v) => update({ workflowId: v })}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Pick a workflow" />
          </SelectTrigger>
          <SelectContent>
            {(workflows ?? []).map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="sw-delay" className="mb-1 block text-xs font-medium text-gray-400">
            Delay (days)
          </label>
          <Input
            id="sw-delay"
            type="number"
            min={0}
            value={data.delayDays ?? ''}
            onChange={(e) =>
              update({
                delayDays: e.target.value === '' ? undefined : parseInt(e.target.value, 10) || 0,
              })
            }
          />
        </div>
        <div>
          <label htmlFor="sw-time" className="mb-1 block text-xs font-medium text-gray-400">
            At time (HH:mm)
          </label>
          <Input
            id="sw-time"
            value={data.atTime ?? ''}
            placeholder="18:00"
            onChange={(e) => update({ atTime: e.target.value || undefined })}
          />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Timezone</label>
        <Select
          value={data.timezone || 'Europe/London'}
          onValueChange={(v) => update({ timezone: v })}
        >
          <SelectTrigger className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONES.map((tz) => (
              <SelectItem key={tz} value={tz}>
                {tz}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-1 text-[10px] text-gray-500">
          &ldquo;2 days at 18:00&rdquo; means 18:00 local time on that day. If the time has already
          passed today with a 0-day delay, the run happens now.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">
          Run at (optional, overrides delay)
        </label>
        <VariableAutocomplete
          value={data.runAt ?? ''}
          onChange={(v) => update({ runAt: v || undefined })}
          placeholder="{{scheduled.payload.follow_up_at}}"
          {...pickerProps}
        />
      </div>

      <div>
        <label htmlFor="sw-offset" className="mb-1 block text-xs font-medium text-gray-400">
          Offset (minutes)
        </label>
        <Input
          id="sw-offset"
          type="number"
          value={data.offsetMinutes ?? ''}
          placeholder="-30"
          onChange={(e) =>
            update({
              offsetMinutes: e.target.value === '' ? undefined : parseInt(e.target.value, 10) || 0,
            })
          }
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Contact id</label>
        <VariableAutocomplete
          value={data.contactId ?? '{{contact.id}}'}
          onChange={(v) => update({ contactId: v || '{{contact.id}}' })}
          {...pickerProps}
        />
      </div>

      <div>
        <label htmlFor="sw-payload" className="mb-1 block text-xs font-medium text-gray-400">
          Payload (JSON, optional)
        </label>
        <textarea
          id="sw-payload"
          className="border-input bg-background min-h-[80px] w-full rounded-md border px-3 py-2 font-mono text-xs"
          value={data.payload ?? ''}
          placeholder='{"lead": {{webhook.body}}}'
          onChange={(e) => update({ payload: e.target.value || undefined })}
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Available in the target workflow as {'{{scheduled.payload.*}}'}.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="sw-dedupe" className="mb-1 block text-xs font-medium text-gray-400">
            Dedupe key
          </label>
          <Input
            id="sw-dedupe"
            value={data.dedupeKey ?? ''}
            placeholder="lost-lead-d2"
            onChange={(e) => update({ dedupeKey: e.target.value || undefined })}
          />
        </div>
        <div>
          <label htmlFor="sw-output" className="mb-1 block text-xs font-medium text-gray-400">
            Output key
          </label>
          <Input
            id="sw-output"
            value={data.outputKey || 'scheduled_run'}
            onChange={(e) => update({ outputKey: e.target.value || 'scheduled_run' })}
          />
        </div>
      </div>
      <p className="text-[10px] text-gray-500">
        With a dedupe key, scheduling again for the same contact replaces the pending run. Pending
        runs are listed under Scheduled Sends &rarr; Workflow runs.
      </p>
    </div>
  );
};
